import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { isAxiosError } from 'axios';
import { useState, type ChangeEvent, type FormEvent } from 'react';
import {
  fetchBalancaConfig,
  saveBalancaConfig,
} from '@/services/balancaConfigService';
import type { IBalancaConfigApiError, IBalancaConfigForm } from '@/types/balancaConfig';
import { readStoredUser } from '@/utils/tenantScope';
import './config-balanca.css';

export const Route = createFileRoute('/_authenticated/configuracao/config-balanca/')({
  component: ConfigBalancaPage,
});

const EMPTY_FORM: IBalancaConfigForm = {
  balanca_id: '',
  lote: '',
  codigo_produto: '',
  validade: '',
  tara: '',
};

function extractApiError(error: unknown, fallback: string): string {
  if (isAxiosError<IBalancaConfigApiError>(error)) {
    const data = error.response?.data;
    if (typeof data?.error === 'string') return data.error;
    if (data?.error && typeof data.error === 'object' && data.error.message) {
      return data.error.message;
    }
    return error.message;
  }
  if (error instanceof Error) return error.message;
  return fallback;
}

function canWrite(): boolean {
  const role = readStoredUser()?.role;
  return role === 'tenant_admin' || role === 'super_admin' || role === 'operator';
}

function ConfigBalancaPage() {
  const writeOk = canWrite();
  const [form, setForm] = useState<IBalancaConfigForm>(EMPTY_FORM);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [loadingExisting, setLoadingExisting] = useState(false);

  const saveMutation = useMutation({
    mutationFn: saveBalancaConfig,
    onSuccess: (data) => {
      setSuccessMessage(data.message || 'Configuração salva com sucesso.');
      setErrorMessage('');
      setForm(EMPTY_FORM);
    },
    onError: (err) => {
      setSuccessMessage('');
      setErrorMessage(extractApiError(err, 'Erro ao salvar configuração.'));
    },
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const loadExisting = async () => {
    const balancaId = form.balanca_id.trim();
    if (!balancaId) return;
    setLoadingExisting(true);
    setErrorMessage('');
    try {
      const row = await fetchBalancaConfig(balancaId);
      setForm({
        balanca_id: row.balanca_id,
        lote: row.lote,
        codigo_produto: row.codigo_produto,
        validade: row.validade,
        tara: String(row.tara),
      });
      setSuccessMessage('Última configuração carregada.');
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 404) {
        setErrorMessage('Nenhuma configuração anterior para esta balança.');
      } else {
        setErrorMessage(extractApiError(err, 'Falha ao carregar configuração.'));
      }
    } finally {
      setLoadingExisting(false);
    }
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!writeOk) return;

    const { balanca_id, lote, codigo_produto, validade, tara } = form;
    if (!balanca_id.trim() || !lote.trim() || !codigo_produto.trim() || !validade || !tara.trim()) {
      setErrorMessage('Todos os campos são obrigatórios.');
      setSuccessMessage('');
      return;
    }

    const taraFloat = parseFloat(tara.replace(',', '.'));
    if (Number.isNaN(taraFloat) || taraFloat < 0) {
      setErrorMessage('O valor da tara é inválido.');
      setSuccessMessage('');
      return;
    }

    saveMutation.mutate({
      balanca_id: balanca_id.trim(),
      lote: lote.trim(),
      codigo_produto: codigo_produto.trim(),
      validade,
      tara: taraFloat.toString(),
    });
  };

  const pending = saveMutation.isPending || loadingExisting;

  return (
    <div className="config-balanca-page">
      <div className="config-balanca-page__header">
        <div>
          <h2>Configurar Balanças</h2>
          <p className="config-balanca-page__subtitle">
            Define tara e contexto (lote, produto, validade) usados na pesagem.
          </p>
        </div>
      </div>

      {successMessage ? (
        <p className="config-balanca-page__success" role="status">
          {successMessage}
        </p>
      ) : null}
      {errorMessage ? (
        <p className="config-balanca-page__error" role="alert">
          {errorMessage}
        </p>
      ) : null}

      {!writeOk ? (
        <p className="config-balanca-page__error" role="alert">
          Seu perfil não pode alterar configuração de balança.
        </p>
      ) : null}

      <form className="config-balanca-page__card" onSubmit={onSubmit}>
        <div className="config-balanca-page__grid">
          <div className="config-balanca-page__field config-balanca-page__field--balanca">
            <label htmlFor="config-balanca-id">ID da Balança</label>
            <div className="config-balanca-page__balanca-row">
              <input
                id="config-balanca-id"
                type="text"
                name="balanca_id"
                value={form.balanca_id}
                onChange={handleChange}
                disabled={!writeOk || pending}
                autoComplete="off"
                required
              />
              <button
                type="button"
                className="config-balanca-page__btn-secondary"
                onClick={() => void loadExisting()}
                disabled={!writeOk || pending || !form.balanca_id.trim()}
              >
                Carregar última
              </button>
            </div>
          </div>

          <div className="config-balanca-page__field">
            <label htmlFor="config-balanca-lote">Lote</label>
            <input
              id="config-balanca-lote"
              type="text"
              name="lote"
              value={form.lote}
              onChange={handleChange}
              disabled={!writeOk || pending}
              autoComplete="off"
              required
            />
          </div>

          <div className="config-balanca-page__field">
            <label htmlFor="config-balanca-codigo">Código do Produto</label>
            <input
              id="config-balanca-codigo"
              type="text"
              name="codigo_produto"
              value={form.codigo_produto}
              onChange={handleChange}
              disabled={!writeOk || pending}
              autoComplete="off"
              required
            />
          </div>

          <div className="config-balanca-page__field">
            <label htmlFor="config-balanca-validade">Validade</label>
            <input
              id="config-balanca-validade"
              type="date"
              name="validade"
              value={form.validade}
              onChange={handleChange}
              disabled={!writeOk || pending}
              required
            />
          </div>

          <div className="config-balanca-page__field">
            <label htmlFor="config-balanca-tara">Tara (kg)</label>
            <input
              id="config-balanca-tara"
              type="text"
              name="tara"
              value={form.tara}
              onChange={handleChange}
              disabled={!writeOk || pending}
              inputMode="decimal"
              placeholder="0.450"
              autoComplete="off"
              required
            />
          </div>
        </div>

        <div className="config-balanca-page__actions">
          <button
            type="button"
            className="config-balanca-page__btn-secondary"
            onClick={() => {
              setForm(EMPTY_FORM);
              setErrorMessage('');
              setSuccessMessage('');
            }}
            disabled={pending}
          >
            Limpar
          </button>
          <button
            type="submit"
            className="config-balanca-page__btn-primary"
            disabled={!writeOk || pending}
          >
            {saveMutation.isPending ? 'Salvando…' : 'Salvar configuração'}
          </button>
        </div>
      </form>
    </div>
  );
}
